/* eslint-disable max-lines */
import {
  GetSnowflakeJobItem,
  GetSnowflakeJobsParams,
  SnowflakeJobFilters,
  WorkflowType,
} from "@/pages/query/types";
import {
  useCustomTagFilters,
  useRegisterCustomTagFilters,
} from "@/pages/query/hooks/useCustomTagFilters";
import {
  appsFilter as appsFilterConfig,
  teamsFilter as teamsFilterConfig,
  workflowIdsFilter,
} from "@/pages/query/utils/snowflakeJobsFilterConfig";
import { FC, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobs } from "@/helpers/apis";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import {
  getWorkflowIdLabel,
  getWorkflowTerminology,
  WorkflowIdCell,
} from "@/pages/query/snowflake";
import { formatBytes, formatCost, formatMilliseconds, getInstanceName } from "@/helpers/utils";
import { cn } from "@/lib/utils";
import { ColorInsightTags } from "@/components/tags";
import { FilterApplicableFor } from "@/components/tags/types";
import { Typography, Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useMemo } from "react";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";

interface CustomWorkloadsJobsTableProps {
  workflowType: WorkflowType;
  filters?: SnowflakeJobFilters;
}

const CustomWorkloadsJobsTable: FC<CustomWorkloadsJobsTableProps> = ({ workflowType, filters }) => {
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    setTotalPages,
    totalItems,
    setTotalItems,
  } = useTablePagination();
  const { getFilterValue } = useDatatableContext();
  const [searchParams] = useSearchParams();

  // Sort state from URL params
  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const { currency, currencySymbol, datastores = [] } = useAppState();

  const costFilter = useMemo(() => {
    return getFilterValue<string>("query_cost") || "";
  }, [getFilterValue]);
  const executionTimeFilter = useMemo(() => {
    return getFilterValue<string>("executionTime") || "";
  }, [getFilterValue]);
  const teamsFilter = useMemo(() => {
    return getFilterValue<string[]>("teams") || [];
  }, [getFilterValue]);

  const appsFilter = useMemo(() => {
    return getFilterValue<string[]>("apps") || [];
  }, [getFilterValue]);

  const workflowFilter = useMemo(() => {
    return getFilterValue<string[]>("workflows") || [];
  }, [getFilterValue]);

  const warehouseFilter = useMemo(() => {
    return getFilterValue<string[]>("warehouses") || [];
  }, [getFilterValue]);

  const instanceFilter = useMemo(() => {
    return getFilterValue<string[]>("instance_id") || [];
  }, [getFilterValue]);

  const customTagFilters = useCustomTagFilters(filters?.custom_tag_keys, getFilterValue);

  const executionTimeFilterMode = useMemo(
    () => getFilterValue<string>("executionTimeFilterMode") || "lt",
    [getFilterValue]
  );
  const costFilterMode = useMemo(
    () => getFilterValue<string>("costFilterMode") || "lt",
    [getFilterValue]
  );

  const { startDate, endDate } = useDateFromSearchParams();

  const getInstanceNameFromId = (instanceId: number) => {
    return getInstanceName(instanceId, datastores);
  };

  // Query parameters
  const getFilterParams = () => {
    const params: Partial<GetSnowflakeJobsParams> = {
      page: page + 1,
      size: pageSize,
      sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      workflows: workflowFilter,
      warehouses: warehouseFilter,
      teams: teamsFilter,
      apps: appsFilter,
      workflow_type: workflowType,
    };

    if (instanceFilter.length > 0) {
      params.instance_id = instanceFilter;
    }

    if (costFilter) {
      params.costFilter = costFilter;
      params.costFilterMode = costFilterMode === "lt" ? "lt" : "gt";
    }

    if (executionTimeFilter) {
      params.executionTimeFilter = parseFloat(executionTimeFilter) * 1000;
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }

    if (Object.keys(customTagFilters).length > 0) {
      params.custom_tag_filters = customTagFilters;
    }

    return params;
  };

  // Fetch job data using OpenSearch query
  const { data, isLoading } = useQuery({
    queryKey: [
      "snowflake_jobs",
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      costFilter,
      costFilterMode,
      ...workflowFilter,
      ...warehouseFilter,
      ...teamsFilter,
      ...appsFilter,
      ...instanceFilter,
      executionTimeFilter,
      executionTimeFilterMode,
      startDate,
      endDate,
      workflowType,
      customTagFilters,
    ],
    queryFn: () => getSnowflakeJobs(getFilterParams()),
  });

  useEffect(() => {
    if (data) {
      setTotalPages(data.pages);
      setTotalItems(data.total);
    }
  }, [data, setTotalItems, setTotalPages]);

  // Register custom tag filters with the context (they appear in "More Filters" sheet)
  useRegisterCustomTagFilters(filters?.custom_tag_keys, workflowType, startDate, endDate);

  const facetCtx = useMemo(
    () => ({ startDate, endDate, workflowType }),
    [startDate, endDate, workflowType]
  );

  // Table headers
  const tableHeaders: TableHeaderType<GetSnowflakeJobItem>[] = [
    {
      id: 1,
      label: getWorkflowIdLabel(workflowType),
      uid: "workflow_id",
      render: (item: GetSnowflakeJobItem) => (
        <WorkflowIdCell item={item} workflowType={workflowType} />
      ),
      filter: {
        searchKey: "workflows",
        label: "Workloads",
        ...workflowIdsFilter(facetCtx),
      },
    },
    {
      id: 2,
      label: "Total Cost",
      uid: "query_cost",
      render: (item: GetSnowflakeJobItem) => <div>{formatCost(item.query_cost, currency)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        filterType: FilterType.TEXT,
        searchKey: "query_cost",
        filterMode: "lt",
        filterModeSearchKey: "costFilterMode",
        placeHolder: `Specify cost in ${currencySymbol}`,
        label: "Total Cost",
      },
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: GetSnowflakeJobItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
    },
    {
      id: 4,
      label: "Execution Time",
      uid: "execution_time",
      render: (item: GetSnowflakeJobItem) => <div>{formatMilliseconds(item.execution_time)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        filterType: FilterType.TEXT,
        searchKey: "executionTime",
        filterMode: "lt",
        filterModeSearchKey: "executionTimeFilterMode",
        placeHolder: "Specify time in seconds",
        label: "Execution Time",
      },
    },
    {
      id: 5,
      label: "Runs",
      uid: "runs",
      render: (item: GetSnowflakeJobItem) => <div>{item.runs}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 6,
      label: "Warehouses",
      uid: "warehouses",
      render: (item: GetSnowflakeJobItem) => (
        <div className={cn("al-break-words", "al-line-clamp-2")}>
          {item.warehouse_names.join(", ")}
        </div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "warehouses",
        label: "Warehouses",
        options:
          filters?.warehouses.map(t => ({
            label: t,
            value: t,
          })) ?? [],
      },
    },
    {
      id: 7,
      label: getWorkflowTerminology(workflowType, "Projects"),
      uid: "teams",
      render: (item: GetSnowflakeJobItem) => <ColorInsightTags tags={item.teams} showMore />,
      filter: {
        searchKey: "teams",
        label: getWorkflowTerminology(workflowType, "Projects"),
        ...teamsFilterConfig(facetCtx, filters?.teams),
      },
    },
    {
      id: 8,
      label: getWorkflowTerminology(workflowType, "Apps"),
      uid: "apps",
      render: (item: GetSnowflakeJobItem) => {
        return <ColorInsightTags tags={item.apps.filter(Boolean)} showMore />;
      },
      filter: {
        searchKey: "apps",
        label: getWorkflowTerminology(workflowType, "Apps"),
        ...appsFilterConfig(facetCtx, filters?.apps),
      },
    },
    {
      id: 9,
      label: "Instance",
      uid: "instance_id",
      render: (item: GetSnowflakeJobItem) => (
        <div className={cn("al-break-words")}>
          {item.instance_id ? getInstanceNameFromId(item.instance_id) : "-"}
        </div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "instance_id",
        label: "Instance",
        options: datastores
          .filter(instance => instance.instance_id != null)
          .map(instance => ({
            label: instance.name,
            value: String(instance.instance_id),
          })),
      },
    },
  ];

  return (
    <Stack direction="column">
      <Stack align="center">
        <Typography variant={"section-heading"}>
          {getWorkflowTerminology(workflowType, "Job List")}
        </Typography>
      </Stack>
      <DataTable
        header={tableHeaders}
        data={data ?? { items: [] }}
        idKey={(item: GetSnowflakeJobItem) => item.workflow_id}
        isLoading={isLoading}
        applicableFor={
          workflowType === WorkflowType.TABLEAU
            ? FilterApplicableFor.TABLEAU_WORKLOADS
            : FilterApplicableFor.SNOWFLAKE_WORKLOADS
        }
        portals={{
          filter: "workloads-filter-container",
        }}
        defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
      />

      <Paginate
        itemCount={totalItems}
        numPages={totalPages}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        showPageSize
      />
    </Stack>
  );
};

export default CustomWorkloadsJobsTable;
